import type { AirdropMerkleData, CreateTokenDetailsFormData, V4Config } from "@repo/shared-types";
import { DEFAULT_V4_CONFIG } from "@repo/shared-types";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";

import { useUploadLogo } from "../../hooks/api/use-upload-logo";
import { useDeployToken } from "../../hooks/chain/use-deploy-token";
import { useWallet } from "../../hooks/use-wallet";
import { useWizardDraft } from "../../hooks/use-wizard-draft";
import { fadeInUp } from "../../lib/animations";
import { getBlockExplorerUrl } from "../../lib/wagmi-config";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";
import { DeploySuccess } from "./DeploySuccess";
import { DetailsStep } from "./DetailsStep";
import { ReviewStep } from "./ReviewStep";
import { TokenPreview } from "./TokenPreview";
import { V4ConfigStep } from "./V4ConfigStep";

type WizardStep = {
	id: number;
	title: string;
	description: string;
	status: "pending" | "current" | "complete";
};

type WizardFormData = {
	details: Partial<CreateTokenDetailsFormData>;
	v4Config: Partial<V4Config>;
	logoPreviewUrl?: string | null;
	airdropMerkleData?: AirdropMerkleData;
	logoUploadId?: string;
};

function isWizardFormData(value: unknown): value is WizardFormData {
	return typeof value === "object" && value !== null && "details" in value && "v4Config" in value;
}

const STEP_DEFINITIONS: WizardStep[] = [
	{
		id: 1,
		title: "Details",
		description: "Basic token information",
		status: "current",
	},
	{
		id: 2,
		title: "V4 Config",
		description: "Uniswap V4 configuration",
		status: "pending",
	},
	{
		id: 3,
		title: "Review & Deploy",
		description: "Review and deploy your token",
		status: "pending",
	},
];

export function CreateTokenWizard() {
	const [currentStep, setCurrentStep] = useState(1);
	const [formData, setFormData] = useState<WizardFormData>({
		details: {},
		v4Config: DEFAULT_V4_CONFIG,
	});
	const [showResumeModal, setShowResumeModal] = useState(false);

	const merkleTreeRef = useRef<unknown>(null);

	const { hasDraft, draftData, saveDraft, clearDraft, resumeDraft } = useWizardDraft();
	const deployToken = useDeployToken();
	const { getAccessToken, address } = useWallet();
	const uploadLogoMutation = useUploadLogo();

	const hasMerkleTree = merkleTreeRef.current !== null;

	// Check for existing draft on mount
	useEffect(() => {
		if (hasDraft && draftData) {
			setShowResumeModal(true);
		}
	}, [hasDraft, draftData]);

	// Save draft on form data change (debounced in hook)
	useEffect(() => {
		if (Object.keys(formData.details).length > 0 || Object.keys(formData.v4Config).length > 0) {
			saveDraft(currentStep, formData);
		}
	}, [formData, currentStep, saveDraft]);

	const isStepComplete = (stepId: number): boolean => {
		switch (stepId) {
			case 1: {
				const d = formData.details;
				return Boolean(d.name && d.symbol && d.description && d.category);
			}
			case 2: {
				const feeSplitSum =
					(formData.v4Config.feeSplitCreator ?? 0) +
					(formData.v4Config.feeSplitTreasury ?? 0) +
					(formData.v4Config.feeSplitProtocol ?? 0);
				return feeSplitSum === 100;
			}
			default:
				return false;
		}
	};

	const getStepStatuses = () => {
		return STEP_DEFINITIONS.map((step) => ({
			...step,
			status:
				step.id < currentStep
					? ("complete" as const)
					: step.id === currentStep
						? ("current" as const)
						: ("pending" as const),
		}));
	};

	const handleStepClick = (targetStep: number) => {
		if (targetStep === currentStep) return;
		if (targetStep < currentStep) {
			setCurrentStep(targetStep);
			return;
		}
		for (let s = currentStep; s < targetStep; s++) {
			if (!isStepComplete(s)) return;
		}
		setCurrentStep(targetStep);
	};

	const handleResumeDraft = () => {
		const draft = resumeDraft();
		if (draft && isWizardFormData(draft.formData)) {
			setCurrentStep(draft.step);
			setFormData(draft.formData);
		}
		setShowResumeModal(false);
	};

	const handleStartFresh = () => {
		clearDraft();
		setShowResumeModal(false);
	};

	const handleDetailsChange = (data: Partial<CreateTokenDetailsFormData>) => {
		// DetailsStep sets logoUploadId on the details object at runtime (via handleUploadComplete).
		// Extract it into the top-level WizardFormData so it can be passed to deploy.
		const record: Record<string, unknown> = { ...data };
		const logoId = typeof record.logoUploadId === "string" ? record.logoUploadId : undefined;
		setFormData((prev) => ({
			...prev,
			details: data,
			...(logoId !== undefined ? { logoUploadId: logoId } : {}),
			...(logoId === undefined && "logoUploadId" in record ? { logoUploadId: undefined } : {}),
		}));
	};

	const handleLogoPreviewChange = (url: string | null) => {
		setFormData((prev) => ({
			...prev,
			logoPreviewUrl: url,
		}));
	};

	const handleNextFromDetails = () => {
		setCurrentStep(2);
	};

	const handleV4ConfigChange = (data: Partial<V4Config>) => {
		setFormData((prev) => ({
			...prev,
			v4Config: { ...prev.v4Config, ...data },
		}));
	};

	const handleAirdropMerkleData = (data: AirdropMerkleData, treeDump: unknown) => {
		merkleTreeRef.current = treeDump;
		setFormData((prev) => ({
			...prev,
			airdropMerkleData: data,
		}));
	};

	const handleNextFromV4 = () => {
		setCurrentStep(3);
	};

	const handleBackFromV4 = () => {
		setCurrentStep(1);
	};

	const handleBackFromReview = () => {
		setCurrentStep(2);
	};

	const uploadFn = useCallback(
		async (file: File): Promise<{ data: { id: string; url: string } }> => {
			const token = await getAccessToken();
			if (!token) {
				// eslint-disable-next-line no-restricted-syntax -- LogoUpload expects thrown errors for error display
				throw new Error("Not authenticated. Please reconnect your wallet.");
			}
			return uploadLogoMutation.mutateAsync({
				file,
				token,
				...(address ? { walletAddress: address } : {}),
			});
		},
		[getAccessToken, uploadLogoMutation, address]
	);

	const renderStepContent = () => {
		switch (currentStep) {
			case 1:
				return (
					<DetailsStep
						data={formData.details}
						onDataChange={handleDetailsChange}
						onNext={handleNextFromDetails}
						uploadFn={uploadFn}
						logoPreviewUrl={formData.logoPreviewUrl ?? null}
						onLogoPreviewChange={handleLogoPreviewChange}
					/>
				);
			case 2:
				return (
					<V4ConfigStep
						data={formData.v4Config}
						onDataChange={handleV4ConfigChange}
						onNext={handleNextFromV4}
						onBack={handleBackFromV4}
						onAirdropMerkleData={handleAirdropMerkleData}
					/>
				);
			case 3: {
				const airdropExt = formData.v4Config.extensions?.find((ext) => ext.type === "Airdrop");
				const airdropRecipients =
					airdropExt && airdropExt.type === "Airdrop" ? airdropExt.recipients : undefined;
				const hasPresale = formData.v4Config.extensions?.some((ext) => ext.type === "Presale");

				if (deployToken.state.status === "confirmed" && deployToken.result.contractAddress) {
					return (
						<DeploySuccess
							tokenName={formData.details.name ?? "Token"}
							tokenSymbol={formData.details.symbol ?? "TKN"}
							contractAddress={deployToken.result.contractAddress}
							poolAddress={deployToken.result.poolAddress}
							tokenId={deployToken.result.tokenId ?? ""}
							blockExplorerUrl={getBlockExplorerUrl()}
							airdropRecipientCount={airdropRecipients?.length}
							airdropRegistered={deployToken.result.airdropRegistered}
							presaleEnabled={hasPresale}
							presaleStarted={deployToken.result.presaleStarted}
						/>
					);
				}
				return (
					<ReviewStep
						details={formData.details}
						v4Config={formData.v4Config}
						logoPreviewUrl={formData.logoPreviewUrl ?? null}
						transactionStatus={deployToken.state.status}
						txHash={deployToken.state.txHash}
						txError={deployToken.state.error}
						blockExplorerUrl={getBlockExplorerUrl()}
						onBack={handleBackFromReview}
						onDeploy={() => {
							void deployToken.deploy({
								details: formData.details,
								v4Config: formData.v4Config,
								logoUploadId: formData.logoUploadId,
								airdropMerkleData: formData.airdropMerkleData,
								merkleTree: merkleTreeRef.current ?? undefined,
							});
						}}
						onRetry={() => {
							deployToken.reset();
						}}
						airdropRecipients={airdropRecipients}
						airdropMerkleReady={
							!airdropRecipients || airdropRecipients.length === 0 || hasMerkleTree
						}
					/>
				);
			}
			default:
				return null;
		}
	};

	return (
		<div className="max-w-7xl mx-auto px-4 py-8">
			{/* Step Indicator */}
			<div className="mb-8">
				<div className="flex items-center justify-center space-x-8">
					{getStepStatuses().map((step, index) => {
						const canNavigate =
							step.id !== currentStep &&
							(step.id < currentStep ||
								Array.from({ length: step.id - currentStep }, (_, i) => currentStep + i).every(
									isStepComplete
								));

						return (
							<div key={step.id} className="flex items-center">
								<button
									type="button"
									className={`flex flex-col items-center ${canNavigate ? "cursor-pointer" : "cursor-default"}`}
									onClick={() => handleStepClick(step.id)}
								>
									<div
										className={`
										w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-colors
										${
											step.status === "complete"
												? "bg-green-500 text-white hover:bg-green-600"
												: step.status === "current"
													? "bg-pastel-vibrant-blue text-white"
													: canNavigate
														? "bg-pastel-light-gray text-pastel-gray hover:bg-pastel-vibrant-blue/20"
														: "bg-pastel-light-gray text-pastel-gray"
										}
									`}
									>
										{step.status === "complete" ? "\u2713" : step.id}
									</div>
									<div className="mt-2 text-center">
										<p
											className={`text-sm font-medium ${
												step.status === "current" ? "text-pastel-black" : "text-pastel-gray"
											}`}
										>
											{step.title}
										</p>
										<p className="text-xs text-pastel-gray">{step.description}</p>
									</div>
								</button>
								{index < STEP_DEFINITIONS.length - 1 && (
									<div
										className={`w-16 h-0.5 mx-4 ${
											step.status === "complete" ? "bg-green-500" : "bg-pastel-border"
										}`}
									/>
								)}
							</div>
						);
					})}
				</div>
			</div>

			{/* Main Content */}
			<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
				<div className="lg:col-span-2">
					<AnimatePresence mode="wait">
						<motion.div
							key={currentStep}
							variants={fadeInUp}
							initial="hidden"
							animate="visible"
							exit="exit"
						>
							{renderStepContent()}
						</motion.div>
					</AnimatePresence>
				</div>

				{/* Live Preview */}
				{currentStep === 1 && (
					<div className="lg:col-span-1">
						<TokenPreview
							name={formData.details.name}
							symbol={formData.details.symbol}
							category={formData.details.category}
							logoPreviewUrl={formData.logoPreviewUrl}
							tagline={formData.details.tagline}
						/>
					</div>
				)}
			</div>

			{/* Resume Draft Modal */}
			<Modal
				isOpen={showResumeModal}
				onClose={() => setShowResumeModal(false)}
				title="Resume Draft?"
			>
				<div className="space-y-4">
					<p className="text-pastel-gray">
						You have a saved draft from{" "}
						{draftData && new Date(draftData.timestamp).toLocaleDateString()}. Would you like to
						resume where you left off or start fresh?
					</p>
					<div className="flex gap-3 justify-end">
						<Button variant="outline" onClick={handleStartFresh}>
							Start Fresh
						</Button>
						<Button onClick={handleResumeDraft}>Resume Draft</Button>
					</div>
				</div>
			</Modal>
		</div>
	);
}
